import { ForbiddenAlertIcon } from "@images/svgIcons/forbiddenAlertIcon"
import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { CurrencyFormattedPrice } from "@modules/currency/components/currencyUtils"
import { StripeAccountErrorItem } from "@modules/finances/components/stripeAccountErrorAlert"
import {
  getPaymentErrorButtonLabel,
  getPaymentErrorContent,
  getPaymentErrorSubtitle,
} from "@modules/finances/hooks/useStripeAccountErrors"
import { StripeErrorCode } from "@modules/finances/types/stripeErrorTypes"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleSubscriptionActions } from "@modules/programBundle/components/programBundleSubscriptionActions"
import { ProgramBundleTrialCard } from "@modules/programBundle/components/programBundleTrialCard"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleAthleteSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import { HUText } from "@modules/ui/components/huText"
import { useTheme } from "styled-components"

type ProgramBundlePriceCardProps = {
  programBundle: ProgramBundleEntity
}

export const ProgramBundlePriceCard: React.FC<ProgramBundlePriceCardProps> = ({ programBundle }) => {
  const theme = useTheme()
  const { isAuthor } = useProgramBundleRights(programBundle)
  const {
    programBundleStripeSubscriptionInfos,
    isUpdating: isUpdatingSubscription,
    tryNewPayment,
    openPaymentSettings,
  } = useProgramBundleAthleteSubscription(programBundle)
  const shouldDisplayImpossiblePurchase =
    programBundle.owner.stripeAccountInError &&
    programBundle.price?.value &&
    programBundle.price.value > 0 &&
    (programBundleStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE ||
      programBundleStripeSubscriptionInfos?.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT)

  return (
    <>
      {!isAuthor && <ProgramBundleTrialCard programBundle={programBundle} />}

      {programBundleStripeSubscriptionInfos?.error && (
        <div className="mb-3">
          <StripeAccountErrorItem
            orientation="vertical"
            alertTitle={word("global.stripeError.paymentProblem.title")}
            alertSubtitle={getPaymentErrorSubtitle(programBundleStripeSubscriptionInfos.error)}
            alertContent={getPaymentErrorContent(programBundleStripeSubscriptionInfos.error)}
            primaryButtonLabel={getPaymentErrorButtonLabel(programBundleStripeSubscriptionInfos.error)}
            primaryButtonOnClickFn={
              programBundleStripeSubscriptionInfos.error.errorCode === StripeErrorCode.AUTHENTICATION_REQUIRED
                ? tryNewPayment
                : openPaymentSettings
            }
            primaryButtonLoading={isUpdatingSubscription}
          />
        </div>
      )}

      {/* Impossible purchase information */}
      {shouldDisplayImpossiblePurchase && (
        <div className="card banner rounded-33 flex align-items-center gap-4" style={{ padding: "24px 28px" }}>
          <div>
            <ForbiddenAlertIcon color={theme.mainBlack} backgroundColor={theme.primaryColor} width={50} height={50} />
          </div>

          <div className="flex flex-column">
            <HUText className="z-2" fontStyle="TM">
              {word("program.price.impossiblePurchase.title")}
            </HUText>
            <HUText className="z-2" fontStyle="BM">
              {word("program.price.impossiblePurchase.content")}
            </HUText>
          </div>
        </div>
      )}

      <div className="card rounded-33 flex flex-column gap-2 overflow-hidden relative">
        {programBundle.price?.value ? (
          <div className="w-full z-2 gap-2 flex flex-column">
            {programBundle.type === ProgramBundleType.VIRTUAL ? (
              <>
                <HUText fontStyle="TM">{word("programBundle.price.membership")}</HUText>
                <div className="flex flex-row align-items-baseline gap-2">
                  <CurrencyFormattedPrice price={programBundle.price} />
                  <div className="flex ">
                    <HUText fontStyle="LL">{word("program.price.pricePerMonth")}</HUText>
                  </div>
                </div>
              </>
            ) : (
              <>
                <HUText fontStyle="TM">{word("program.price.singlePurchase")}</HUText>
                <div className="flex flex-row align-items-baseline gap-2">
                  <CurrencyFormattedPrice price={programBundle.price} />
                  <div className="flex">
                    <HUText fontStyle="LL">{word("program.price.onlyOnce")}</HUText>
                  </div>
                </div>
              </>
            )}

            <HUText fontStyle="BM">{word("programBundle.price.payingForAll")}</HUText>
          </div>
        ) : (
          <div className="flex flex-column gap-2 w-full z-2">
            <HUText fontStyle="TM">{word("program.price.free")}</HUText>
            <HUText fontStyle="BM">{word("programBundle.price.freeForAll")}</HUText>
          </div>
        )}

        {isAuthor && programBundle.trialDuration && (
          <HUText className="z-2" fontStyle="BM">
            {`${word("programBundle.price.trial.description", { count: programBundle.trialDuration })}`}
          </HUText>
        )}

        {!isAuthor && (
          <div className="z-2 w-full">
            <ProgramBundleSubscriptionActions programBundle={programBundle} />
          </div>
        )}

        <HustleupSmallIcon
          className="absolute top-0 bg-contain h-full"
          color={theme.mainWhite}
          style={{ opacity: 0.3, right: "-40px" }}
        />
      </div>
    </>
  )
}
